import { Game } from "./game";
import { Team } from "./team";
import { User } from "./user";
import { Tournament } from "./showcase";
import { Sport } from "./conference";
import { ThirdPartyTournament } from "./third_parties";
import { environment } from "src/environments/environment";


export const stripeKey = 'pk_live_ATDuYoJBSoWJ80tmDEjCU9SR';
// export const stripeKey = 'pk_test_IxIOMproyYlzhOLt0gQkN1Pp';

export interface StripePayment extends StripePaymentExtraInfo {
  _id?: string;
  userid?: User['_id'];
  email: string;
  amount: number;
  description: string;
  token?: string;
  customerid?: string;
  type?: number;
  refunded?: boolean;
  createdAt?: Date;
  oldid?: number;
}

export interface StripePaymentExtraInfo {
  teamid?: Team['_id'];
  gameid?: Game['_id'];
  tournamentid?: Tournament['_id'];
  thirdPartyTournamentid?: ThirdPartyTournament['_id'];
  invitepaid?: boolean;
  seasonid?: number;
  breakdownTurnaroundTime?: number;
  breakdownType?: 'Basic' | 'Advanced';
  breakdownGameLimit?: number;
  musicid?: string;
}

export interface CustomerPrice {
  _id?: string;
  sportid: Sport;
  youth?: boolean;
  price: number;
  type: PriceType;
}

export type PriceType = 'platform' | 'fanseasonpass' | 'fansinglegame' | 'storage';

export interface PaymentType {
  name: string;
  value: number;
}

export enum PaymentTypes {
  platformService = 0,
  videoService = 1, 
  fanAccess = 2,
  pastSeasonAccess = 3,
  singleGamePPV = 4,
  tournamentPass = 5,
  pastSeasonRenewal = 6,
  singleUserPlatform = 7,
  singleGameStatBreakdown = 8,
  seasonStatBreakdown = 9,
  qwikcutShowdownVideo = 10,
  additionalStorageHours = 11,
  qwikcutProductPurchase = 12,
  teamAcessSubscription = 13,
  parentAccount = 14,
  thirdPartyTournament = 15,
  apsScoutingEvaluation = 16
}

export const paymentTypes: PaymentType[] = [{
  name: 'Platform Service',
  value: 0,
}, {
  name: 'Video Service',
  value: 1,
}, {
  name: 'Fan Access',
  value: 2,
}, {
  name: 'Past Season Access',
  value: 3,
}, {
  name: 'Single Game PPV',
  value: 4,
}, {
  name: 'Tournament Pass',
  value: 5,
}, {
  name: 'Past-Season Renewal',
  value: 6,
}, {
  name: 'Single-User Platform',
  value: 7,
}, {
  name: 'Single-Game Stat Breakdown',
  value: 8,
}, {
  name: 'Season Stat Breakdown',
  value: 9,
}, {
  name: 'QwikCut Showdown Video',
  value: 10,
}, {
  name: 'Additional Storage Hours',
  value: 11,
}, {
  name: 'QwikCut Product Purchase',
  value: 12,
}, {
  name: 'Team Access Subscription',
  value: 13,
}, {
  name: 'Parent Access',
  value: 14,
}, {
  name: 'Third Party Tournament',
  value: 15,
}, {
  name: 'APS Scouting Evaluation',
  value: 16,
}];

export interface StripeSetupIntent {
  id: string;
  object: string;
  application?: string | null;
  attach_to_self?: boolean | null;
  automatic_payment_methods?: {
    allow_redirects?: 'always' | 'never' | null;
    enabled?: boolean | null;
  } | null;
  cancellation_reason?: 'abandoned' | 'requested_by_customer' | 'duplicate' | null;
  client_secret?: string | null;
  created: number;
  customer?: string | null;
  description?: string | null;
  flow_directions?: Array<'inbound' | 'outbound'> | null;
  last_setup_error?: any; // You can expand this with a detailed interface if needed
  latest_attempt?: string | null;
  livemode: boolean;
  mandate?: string | null;
  metadata?: { [key: string]: any } | null;
  next_action?: any; // You can expand this with a detailed interface if needed
  on_behalf_of?: string | null;
  payment_method?: string | null;
  payment_method_configuration_details?: {
    id: string;
    parent?: string | null;
  } | null;
  payment_method_options?: {
    card?: {
      mandate_options?: any | null;
      network?: string | null;
      request_three_d_secure?: 'any' | 'automatic' | 'challenge' | null;
    };
    [key: string]: any;
  } | null;
  payment_method_types: string[];
  single_use_mandate?: string | null;
  status: 'requires_payment_method' | 'requires_confirmation' | 'requires_action' | 'processing' | 'canceled' | 'succeeded';
  usage: 'on_session' | 'off_session';
}

export interface StripePaymentIntent {
  id: string;
  object: string;
  amount: number;
  amount_capturable: number;
  amount_details?: {
    tip?: {
      amount?: number | null;
    } | null;
  } | null;
  amount_received: number;
  application?: string | null;
  application_fee_amount?: number | null;
  automatic_payment_methods?: {
    allow_redirects?: 'always' | 'never' | null;
    enabled: boolean;
  } | null;
  canceled_at?: number | null;
  cancellation_reason?: 'abandoned' | 'automatic' | 'duplicate' | 'expired' | 'failed_invoice' | 'fraudulent' | 'requested_by_customer' | 'void_invoice' | null;
  capture_method: 'automatic' | 'automatic_async' | 'manual';
  client_secret?: string | null;
  confirmation_method: 'automatic' | 'manual';
  created: number;
  currency: string;
  customer?: string | null;
  description?: string | null;
  last_payment_error?: any; // Expand as needed
  latest_charge?: string | null;
  livemode: boolean;
  metadata?: { [key: string]: any };
  next_action?: any; // Expand as needed
  on_behalf_of?: string | null;
  payment_method?: string | null;
  payment_method_configuration_details?: {
    id: string;
    parent?: string | null;
  } | null;
  payment_method_options?: { [key: string]: any } | null;
  payment_method_types: string[];
  processing?: any | null;
  receipt_email?: string | null;
  review?: string | null;
  setup_future_usage?: 'off_session' | 'on_session' | null;
  shipping?: any | null;
  source?: any | null;
  statement_descriptor?: string | null;
  statement_descriptor_suffix?: string | null;
  status: 'requires_payment_method' | 'requires_confirmation' | 'requires_action' | 'processing' | 'requires_capture' | 'canceled' | 'succeeded';
  transfer_data?: {
    amount?: number | null;
    destination?: string;
  } | null;
  transfer_group?: string | null;
}